// utils/activityTracker.js
// Auto-tracks visitor activities on demo stores:
// - Modal funnel events (close cross button, plan selection, OTP verification, domain submission)
// - Store browsing activities (clicks, scrolls, navigations)

const API = process.env.REACT_APP_API_URL || "/api";
const SESSION_KEY = "oz_activity_sid";
const START_TIME_KEY = "oz_session_start";

function storage(kind) {
  try { return window[kind]; } catch { return null; }
}

export function getSessionId() {
  const store = storage("sessionStorage");
  let sid = store ? store.getItem(SESSION_KEY) : null;
  if (!sid) {
    sid = "oz_s_" + Math.random().toString(36).slice(2, 10) + "_" + Date.now().toString(36);
    try { store && store.setItem(SESSION_KEY, sid); } catch {}
  }
  return sid;
}

export function getSessionStartTime() {
  const store = storage("sessionStorage");
  let start = store ? Number(store.getItem(START_TIME_KEY)) : 0;
  if (!start || isNaN(start)) {
    start = Date.now();
    try { store && store.setItem(START_TIME_KEY, String(start)); } catch {}
  }
  return start;
}

export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remS = s % 60;
  if (m < 60) return remS > 0 ? `${m}m ${remS}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

let activeStoreSlug = "";
export function setActiveStoreSlug(slug) {
  if (slug) activeStoreSlug = slug;
}

const queue = [];
let flushTimer = null;

function sendPayload(payload) {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(`${API}/analytics/track`, blob)) return;
    }
  } catch {}
  fetch(`${API}/analytics/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function flushQueue() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!queue.length) return;
  const events = queue.splice(0, queue.length);
  sendPayload({
    sessionId: getSessionId(),
    site: activeStoreSlug || "ownezzy",
    events,
  });
}

function scheduleFlush() {
  if (queue.length >= 6) {
    flushQueue();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushQueue, 2500);
  }
}

export function trackActivity(type, target, meta = {}, path = "") {
  const currentPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  queue.push({
    type,
    target: String(target || "").slice(0, 200),
    path: currentPath,
    meta,
    timestamp: Date.now(),
  });
  scheduleFlush();
}

export function trackFunnelEvent(type, target, meta = {}, path = "") {
  const currentPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  sendPayload({
    sessionId: getSessionId(),
    site: activeStoreSlug || meta.site || "ownezzy",
    events: [
      {
        type,
        target: String(target || "").slice(0, 200),
        path: currentPath,
        meta,
        timestamp: Date.now(),
      },
    ],
  });
}

// Milestone flags for scroll tracking on the current page
const reachedMilestones = new Set();
let lastTrackedPath = "";

export function resetScrollMilestones(newPath = "") {
  reachedMilestones.clear();
  lastTrackedPath = newPath;
}

let initialized = false;
export function initActivityTracker(getSlugFn) {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  if (typeof getSlugFn === "function") {
    const slug = getSlugFn();
    if (slug) activeStoreSlug = slug;
  }

  // Initial session heartbeat
  sendPayload({
    sessionId: getSessionId(),
    site: activeStoreSlug || "ownezzy",
    events: [],
  });

  // Track clicks on interactive elements
  document.addEventListener(
    "click",
    (e) => {
      try {
        const target = e.target;
        if (!target) return;

        // Skip sensitive password inputs
        if (target.type === "password" || (target.tagName === "INPUT" && target.name?.toLowerCase().includes("password"))) return;

        const interactive = target.closest(
          "button, a, input[type='button'], input[type='submit'], [role='button'], .product-card, .ozsf-plan, .ozsf-toggle button, .ozsf-close, .ozsf-primary, .category-item, .nav-link"
        );

        if (!interactive) return;

        // Ignore clicks inside the sign-up (shared SignupFlow, which has explicit tracking)
        if (interactive.closest(".ozsf-modal") && !interactive.classList.contains("ozsf-close")) {
          return;
        }

        let label = (
          interactive.getAttribute("aria-label") ||
          interactive.getAttribute("title") ||
          interactive.innerText ||
          interactive.value ||
          interactive.className ||
          interactive.tagName
        ).trim().replace(/\s+/g, " ");

        if (!label) label = interactive.tagName;
        if (label.length > 80) label = label.slice(0, 80) + "…";

        trackActivity("click", `Clicked: ${label}`, {
          tag: interactive.tagName,
          id: interactive.id || undefined,
          className: String(interactive.className).slice(0, 50) || undefined,
        });
      } catch {}
    },
    { passive: true, capture: true }
  );

  // Track scroll depth milestones (25%, 50%, 75%, 100%)
  let scrollThrottle = null;
  window.addEventListener(
    "scroll",
    () => {
      if (scrollThrottle) return;
      scrollThrottle = setTimeout(() => {
        scrollThrottle = null;
        try {
          const docHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight
          );
          const scrollPos = window.scrollY + window.innerHeight;
          const pct = Math.round((scrollPos / (docHeight || 1)) * 100);

          const milestones = [25, 50, 75, 100];
          for (const m of milestones) {
            if (pct >= m && !reachedMilestones.has(m)) {
              reachedMilestones.add(m);
              trackActivity("scroll", `Scrolled ${m}% depth`, { percent: m });
            }
          }
        } catch {}
      }, 300);
    },
    { passive: true }
  );

  // Initialize session start time immediately
  getSessionStartTime();

  let lastExitTime = 0;
  let lastExitAction = "";

  function trackExit(isClose = false) {
    const now = Date.now();
    if (now - lastExitTime < 1200 && (lastExitAction === "closed" || !isClose)) {
      return;
    }
    lastExitTime = now;
    lastExitAction = isClose ? "closed" : "left";

    const start = getSessionStartTime();
    const elapsedSec = Math.max(1, Math.round((now - start) / 1000));
    const timeSpent = formatDuration(elapsedSec);

    const target = isClose
      ? `Closed ownezzy website · Total time spent: ${timeSpent}`
      : `Left ownezzy website · Total time spent: ${timeSpent}`;

    const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

    queue.push({
      type: "site_leave",
      target,
      path: currentPath,
      meta: {
        action: isClose ? "closed" : "left",
        timeSpent,
        durationSeconds: elapsedSec,
        durationFormatted: timeSpent,
      },
      timestamp: now,
    });

    flushQueue();
  }

  // Track user leaving or closing the website
  window.addEventListener("pagehide", () => trackExit(true));
  window.addEventListener("beforeunload", () => trackExit(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      trackExit(false);
    } else if (document.visibilityState === "visible") {
      lastExitAction = "";
    }
  });
}
